if (!customElements.get('quick-cart-button')) {
  class QuickCartButton extends HTMLElement {
    constructor() {
      super();

      this.variantId = Number(this.dataset.variantId);

      this.addButton = this.querySelector('.quick-cart__add');
      this.addText = this.querySelector('.quick-cart__add-text');
      this.loader = this.querySelector('.quick-cart__loader');

      this.quantityWrapper = this.querySelector(
        '.quick-cart__quantity'
      );
      this.quantityValue = this.querySelector(
        '.quick-cart__value'
      );

      this.minusButton = this.querySelector(
        '.quick-cart__minus'
      );
      this.plusButton = this.querySelector(
        '.quick-cart__plus'
      );

      this.errorElement = this.querySelector(
        '.quick-cart__error'
      );

      this.quantity = 0;
      this.lineItemKey = null;
      this.loading = false;

      this.handleAdd = this.handleAdd.bind(this);
      this.handlePlus = this.handlePlus.bind(this);
      this.handleMinus = this.handleMinus.bind(this);
    }

    connectedCallback() {
      if (!this.variantId || !this.addButton) return;

      this.addButton.addEventListener(
        'click',
        this.handleAdd
      );

      this.plusButton?.addEventListener(
        'click',
        this.handlePlus
      );

      this.minusButton?.addEventListener(
        'click',
        this.handleMinus
      );

      this.syncWithCart();
    }

    disconnectedCallback() {
      this.addButton?.removeEventListener(
        'click',
        this.handleAdd
      );

      this.plusButton?.removeEventListener(
        'click',
        this.handlePlus
      );

      this.minusButton?.removeEventListener(
        'click',
        this.handleMinus
      );
    }

    get rootUrl() {
      return window.Shopify?.routes?.root || '/';
    }

    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(options.headers || {})
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          'Unable to update the cart.'
        );
      }

      return data;
    }

    async getCart() {
      return this.fetchJSON(
        `${this.rootUrl}cart.js`,
        {
          method: 'GET'
        }
      );
    }

    findLineItem(cart) {
      return cart.items.find(
        item => Number(item.variant_id) === this.variantId
      );
    }

    async syncWithCart() {
      try {
        const cart = await this.getCart();
        const lineItem = this.findLineItem(cart);

        if (lineItem) {
          this.quantity = lineItem.quantity;
          this.lineItemKey = lineItem.key;
          this.showQuantitySelector();
        } else {
          this.quantity = 0;
          this.lineItemKey = null;
          this.showAddButton();
        }

        this.updateCartBubble(cart.item_count);
      } catch (error) {
        console.error('Quick cart sync error:', error);
      }
    }

    async handleAdd() {
      if (this.loading) return;

      this.setLoading(true);
      this.hideError();

      try {
        await this.fetchJSON(
          `${this.rootUrl}cart/add.js`,
          {
            method: 'POST',
            body: JSON.stringify({
              items: [
                {
                  id: this.variantId,
                  quantity: 1
                }
              ]
            })
          }
        );

        const cart = await this.getCart();
        const lineItem = this.findLineItem(cart);

        if (!lineItem) {
          throw new Error(
            'The product was added, but its cart item could not be found.'
          );
        }

        this.quantity = lineItem.quantity;
        this.lineItemKey = lineItem.key;

        this.showQuantitySelector();
        this.updateCartBubble(cart.item_count);

        /*
         * Refresh and open the drawer only after
         * the first Add to cart click.
         */
        await this.refreshThemeCart(cart, true);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    async handlePlus() {
      if (this.loading) return;

      await this.changeQuantity(this.quantity + 1);
    }

    async handleMinus() {
      if (this.loading) return;

      await this.changeQuantity(
        Math.max(0, this.quantity - 1)
      );
    }

    async changeQuantity(newQuantity) {
      if (!this.lineItemKey) {
        if (newQuantity > 0) {
          await this.handleAdd();
        }

        return;
      }

      this.setLoading(true);
      this.hideError();

      const previousQuantity = this.quantity;

      /*
       * Optimistic visual update.
       */
      this.quantity = newQuantity;
      this.renderQuantity();

      try {
        const cart = await this.fetchJSON(
          `${this.rootUrl}cart/change.js`,
          {
            method: 'POST',
            body: JSON.stringify({
              id: this.lineItemKey,
              quantity: newQuantity
            })
          }
        );

        const lineItem = this.findLineItem(cart);

        if (lineItem) {
          this.quantity = lineItem.quantity;
          this.lineItemKey = lineItem.key;
          this.showQuantitySelector();
        } else {
          this.quantity = 0;
          this.lineItemKey = null;
          this.showAddButton();
        }

        this.updateCartBubble(cart.item_count);

        /*
         * Refresh the theme cart content, but do
         * not open the drawer for plus/minus.
         */
        await this.refreshThemeCart(cart, false);
      } catch (error) {
        this.quantity = previousQuantity;
        this.renderQuantity();
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    showAddButton() {
      if (this.addButton) {
        this.addButton.hidden = false;
      }

      if (this.quantityWrapper) {
        this.quantityWrapper.hidden = true;
      }
    }

    showQuantitySelector() {
      if (this.addButton) {
        this.addButton.hidden = true;
      }

      if (this.quantityWrapper) {
        this.quantityWrapper.hidden = false;
      }

      this.renderQuantity();
    }

    renderQuantity() {
      if (this.quantityValue) {
        this.quantityValue.textContent = String(
          this.quantity
        );
      }

      if (this.minusButton) {
        this.minusButton.setAttribute(
          'aria-label',
          this.quantity <= 1
            ? 'Remove product from cart'
            : 'Decrease quantity'
        );
      }
    }

    setLoading(status) {
      this.loading = status;
      this.classList.toggle('is-loading', status);

      if (this.addButton) {
        this.addButton.disabled = status;

        if (this.loader) {
          this.loader.hidden = !status;
        }

        if (this.addText) {
          this.addText.style.opacity = status ? '0' : '1';
        }
      }

      if (this.plusButton) {
        this.plusButton.disabled = status;
      }

      if (this.minusButton) {
        this.minusButton.disabled = status;
      }
    }

    updateCartBubble(itemCount) {
      /*
       * Common selectors used by Sleek and
       * other Shopify themes.
       */
      const countElements = document.querySelectorAll(
        [
          '[data-cart-count]',
          '[data-cart-item-count]',
          '.cart-count',
          '.cart-count-bubble',
          '.header__cart-count',
          '.m-cart-count',
          '#cart-icon-bubble .cart-count-bubble span',
          '[aria-label*="cart" i] .cart-count-bubble'
        ].join(',')
      );

      countElements.forEach(element => {
        /*
         * Avoid replacing an entire bubble that
         * contains visually-hidden accessibility text.
         */
        const numberElement =
          element.matches('span') ||
          element.children.length === 0
            ? element
            : element.querySelector(
                '[data-cart-count-number], span:first-child'
              );

        if (numberElement) {
          numberElement.textContent = String(itemCount);
        } else {
          element.textContent = String(itemCount);
        }

        element.hidden = itemCount <= 0;
        element.classList.toggle(
          'hidden',
          itemCount <= 0
        );
      });

      document.documentElement.style.setProperty(
        '--cart-count',
        `"${itemCount}"`
      );
    }

    async refreshThemeCart(cart, shouldOpen) {
      /*
       * Events commonly supported by Shopify themes.
       * Sleek can listen to one of these events and
       * refresh its own cart drawer.
       */
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart,
            source: 'quick-cart-button',
            open: shouldOpen
          }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart,
            open: shouldOpen
          }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            cart,
            open: shouldOpen
          }
        })
      );

      if (!shouldOpen) return;

      /*
       * Try Sleek/custom-element drawer methods first.
       */
      const drawer =
        document.querySelector('cart-drawer') ||
        document.querySelector('m-cart-drawer') ||
        document.querySelector('[data-cart-drawer]') ||
        document.querySelector('#CartDrawer');

      if (drawer) {
        if (typeof drawer.open === 'function') {
          drawer.open();
          return;
        }

        if (typeof drawer.show === 'function') {
          drawer.show();
          return;
        }

        if (typeof drawer.openDrawer === 'function') {
          drawer.openDrawer();
          return;
        }
      }

      /*
       * Fallback: click the existing theme cart
       * drawer trigger without navigating to /cart.
       */
      const drawerTrigger = document.querySelector(
        [
          '[data-cart-drawer-toggle]',
          '[data-open-cart-drawer]',
          '[data-cart-toggle]',
          '.m-cart-drawer--trigger',
          'button[aria-controls*="cart" i]',
          'button[aria-label*="cart" i]'
        ].join(',')
      );

      if (drawerTrigger) {
        drawerTrigger.click();
        window.location.href = "/cart";
      }
    }

    showError(message) {
      if (!this.errorElement) {
        console.error(message);
        return;
      }

      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    hideError() {
      if (!this.errorElement) return;

      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }
  }

  customElements.define(
    'quick-cart-button',
    QuickCartButton
  );
}